import { useCallback, useEffect } from "react";
import { actions, defaultColumn, ensurePluginOrder, makePropGetter, useGetLatest, useMountedLayoutEffect } from "react-table";


// Default Column
defaultColumn.canResize = true;

// Actions
actions.columnStartResizing = "columnStartResizing";
actions.columnResizing = "columnResizing";
actions.columnDoneResizing = "columnDoneResizing";
actions.resetResize = "resetResize";
actions.setResize = "setResize";

export const emptyRenderer = () => <>&nbsp;</>;

// Find the depth of the columns
export function findMaxDepth(columns, depth = 0) {
  return columns.reduce((prev, curr) => {
    if (curr.columns) {
      return Math.max(prev, findMaxDepth(curr.columns, depth + 1));
    }
    return depth;
  }, 0);
}

// Build the visible columns, headers and flat column list
export function linkColumnStructure(columns, parent, depth = 0) {
  return columns.map(column => {
    column = {
      ...column,
      parent,
      depth,
    };

    assignColumnAccessor(column);

    if (column.columns) {
      column.columns = linkColumnStructure(column.columns, column, depth + 1);
    }
    return column;
  });
}

export function flattenColumns(columns) {
  return flattenBy(columns, "columns");
}

export function assignColumnAccessor(column) {
  // First check for string accessor
  let {
    id,
    accessor,
    Header,
  } = column;

  if (typeof accessor === "string") {
    id = id || accessor;
    const accessorPath = accessor.split(".");
    accessor = row => getBy(row, accessorPath);
  }

  if (!id && typeof Header === "string" && Header) {
    id = Header;
  }

  if (!id && column.columns) {
    console.error(column);
    throw new Error("A column ID (or unique \"Header\" value) is required!");
  }

  if (!id) {
    console.error(column);
    throw new Error("A column ID (or string accessor) is required!");
  }

  Object.assign(column, {
    id,
    accessor,
  });

  return column;
}

export function decorateColumn(column, userDefaultColumn) {
  if (!userDefaultColumn) {
    throw new Error();
  }
  Object.assign(column, {
    // Make sure there is a fallback header, just in case
    Header: emptyRenderer,
    Footer: emptyRenderer,
    ...defaultColumn,
    ...userDefaultColumn,
    ...column,
  });

  Object.assign(column, {
    originalWidth: column.width,
  });

  return column;
}

// Build the header groups from the bottom up
export function makeHeaderGroups(
  allColumns,
  defaultColumn,
  additionalHeaderProperties = () => ({}),
) {
  const headerGroups = [];

  let scanColumns = allColumns;

  let uid = 0;
  const getUID = () => uid++;

  while (scanColumns.length) {
    // The header group we are creating
    const headerGroup = {
      headers: [],
    };

    // The parent columns we're going to scan next
    const parentColumns = [];

    const hasParents = scanColumns.some(d => d.parent);

    // Scan each column for parents
    scanColumns.forEach(column => {
      // What is the latest (last) parent column?
      let latestParentColumn = [ ...parentColumns ].reverse()[0];

      let newParent;

      if (hasParents) {
        // If the column has a parent, add it if necessary
        if (column.parent) {
          newParent = {
            ...column.parent,
            originalId: column.parent.id,
            id: `${column.parent.id}_${getUID()}`,
            headers: [ column ],
            ...additionalHeaderProperties(column),
          };
        } else {
          // If other columns have parents, we'll need to add a place holder if necessary
          const originalId = `${column.id}_placeholder`;
          newParent = decorateColumn(
            {
              originalId,
              id: `${column.id}_placeholder_${getUID()}`,
              placeholderOf: column,
              headers: [ column ],
              ...additionalHeaderProperties(column),
            },
            defaultColumn,
          );
        }

        // If the resulting parent columns are the same, just add
        // the column and increment the header span
        if (
          latestParentColumn &&
          latestParentColumn.originalId === newParent.originalId
        ) {
          latestParentColumn.headers.push(column);
        } else {
          parentColumns.push(newParent);
        }
      }

      headerGroup.headers.push(column);
    });

    headerGroups.push(headerGroup);

    // Start scanning the parent columns
    scanColumns = parentColumns;
  }

  return headerGroups.reverse();
}

const pathObjCache = new Map();

export function getBy(obj, path, def) {
  if (!path) {
    return obj;
  }
  const cacheKey = typeof path === "function" ? path : JSON.stringify(path);

  const pathObj =
    pathObjCache.get(cacheKey) ||
    (() => {
      const pathObj = makePathArray(path);
      pathObjCache.set(cacheKey, pathObj);
      return pathObj;
    })();

  let val;

  try {
    val = pathObj.reduce((cursor, pathPart) => cursor[pathPart], obj);
  } catch (e) {
    // continue regardless of error
  }
  return typeof val !== "undefined" ? val : def;
}

export function getFirstDefined(...args) {
  for (let i = 0; i < args.length; i += 1) {
    if (typeof args[i] !== "undefined") {
      return args[i];
    }
  }
}

export function flattenBy(arr, key) {
  const flat = [];

  const recurse = arr => {
    arr.forEach(d => {
      if (!d[key]) {
        flat.push(d);
      } else {
        recurse(d[key]);
      }
    });
  };

  recurse(arr);

  return flat;
}

export function expandRows(
  rows,
  {
    manualExpandedKey,
    expanded,
    expandSubRows = true,
  },
) {
  const expandedRows = [];

  const handleRow = row => {
    row.isExpanded =
      (row.original && row.original[manualExpandedKey]) || expanded[row.id];

    row.canExpand = row.subRows && !!row.subRows.length;

    expandedRows.push(row);

    if (expandSubRows && row.subRows && row.subRows.length && row.isExpanded) {
      row.subRows.forEach(handleRow);
    }
  };

  rows.forEach(handleRow);

  return expandedRows;
}

export function shouldAutoRemoveFilter(autoRemove, value, column) {
  return autoRemove ? autoRemove(value, column) : typeof value === "undefined";
}

export function unpreparedAccessWarning() {
  throw new Error(
    "React-Table: You have not called prepareRow(row) one or more rows you are attempting to render.",
  );
}

let passiveSupported = null;

export function passiveEventSupported() {
  // memoize support to avoid adding multiple test events
  if (typeof passiveSupported === "boolean") return passiveSupported;

  let supported = false;
  try {
    const options = {
      get passive() {
        supported = true;
        return false;
      },
    };

    window.addEventListener("test", null, options);
    window.removeEventListener("test", null, options);
  } catch (err) {
    supported = false;
  }
  passiveSupported = supported;
  return passiveSupported;
}

const reOpenBracket = /\[/g;
const reCloseBracket = /\]/g;

function makePathArray(obj) {
  return (
    flattenDeep(obj)
      // remove all periods in parts
      .map(d => String(d)
        .replace(".", "_"))
      // join parts using period
      .join(".")
      // replace brackets with periods
      .replace(reOpenBracket, ".")
      .replace(reCloseBracket, "")
      // split it back out on periods
      .split(".")
  );
}

function flattenDeep(arr, newArr = []) {
  if (!Array.isArray(arr)) {
    newArr.push(arr);
  } else {
    for (let i = 0; i < arr.length; i += 1) {
      flattenDeep(arr[i], newArr);
    }
  }
  return newArr;
}

const context = {};

export const useResizeColumns = hooks => {
  hooks.getResizerProps = [ defaultGetResizerProps ];
  hooks.getHeaderProps.push({
    style: {
      position: "relative",
    },
  });
  hooks.stateReducers.push(reducer.bind(context));
  hooks.useInstance.push(useInstance, instance => {
    context.instance = instance;
  });
  hooks.useInstanceBeforeDimensions.push(useInstanceBeforeDimensions);
};

const defaultGetResizerProps = (props, {
  instance,
  header,
}) => {
  const { dispatch } = instance;

  const onResizeStart = (e, header) => {
    let isTouchEvent = false;
    if (e.type === "touchstart") {
      // lets not respond to multiple touches (e.g. 2 or 3 fingers)
      if (e.touches && e.touches.length > 1) {
        return;
      }
      isTouchEvent = true;
    }
    const headersToResize = getLeafHeaders(header);
    const headerIdWidths = headersToResize.map(d => [ d.id, d.totalWidth ]);

    const clientX = isTouchEvent ? Math.round(e.touches[0].clientX) : e.clientX;

    const dispatchMove = clientXPos => {
      dispatch({
        type: actions.columnResizing,
        clientX: clientXPos,
      });
    };
    const dispatchEnd = () => dispatch({ type: actions.columnDoneResizing });

    const handlersAndEvents = {
      mouse: {
        moveEvent: "mousemove",
        moveHandler: e => dispatchMove(e.clientX),
        upEvent: "mouseup",
        upHandler: e => {
          document.removeEventListener(
            "mousemove",
            handlersAndEvents.mouse.moveHandler,
          );
          document.removeEventListener(
            "mouseup",
            handlersAndEvents.mouse.upHandler,
          );
          dispatchEnd();
        },
      },
      touch: {
        moveEvent: "touchmove",
        moveHandler: e => {
          if (e.cancelable) {
            e.preventDefault();
            e.stopPropagation();
          }
          dispatchMove(e.touches[0].clientX);
          return false;
        },
        upEvent: "touchend",
        upHandler: e => {
          document.removeEventListener(
            handlersAndEvents.touch.moveEvent,
            handlersAndEvents.touch.moveHandler,
          );
          document.removeEventListener(
            handlersAndEvents.touch.upEvent,
            handlersAndEvents.touch.moveHandler,
          );
          dispatchEnd();
        },
      },
    };

    const events = isTouchEvent
      ? handlersAndEvents.touch
      : handlersAndEvents.mouse;
    const passiveIfSupported = passiveEventSupported()
      ? { passive: false }
      : false;
    document.addEventListener(
      events.moveEvent,
      events.moveHandler,
      passiveIfSupported,
    );
    document.addEventListener(
      events.upEvent,
      events.upHandler,
      passiveIfSupported,
    );

    dispatch({
      type: actions.columnStartResizing,
      columnId: header.id,
      columnWidth: header.totalWidth,
      headerIdWidths,
      clientX,
    });
  };

  return [
    props,
    {
      onMouseDown: e => e.persist() || onResizeStart(e, header),
      onTouchStart: e => e.persist() || onResizeStart(e, header),
      style: {
        cursor: "ew-resize",
      },
      draggable: false,
      role: "separator",
    },
  ];
};

useResizeColumns.pluginName = "useResizeColumns";

function reducer(state, action) {
  if (action.type === actions.init) {
    return {
      columnResizing: {
        columnWidths: {},
      },
      ...state,
    };
  }

  if (action.type === actions.resetResize) {
    return {
      ...state,
      columnResizing: {
        columnWidths: {},
      },
    };
  }

  if (action.type === actions.setResize) {
    return {
      ...state,
      columnResizing: action.payload,
    };
  }

  if (action.type === actions.columnStartResizing) {
    const {
      clientX,
      columnId,
      columnWidth,
      headerIdWidths,
    } = action;

    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        startX: clientX,
        headerIdWidths,
        columnWidth,
        isResizingColumn: columnId,
      },
    };
  }

  if (action.type === actions.columnResizing) {
    const { clientX } = action;
    const {
      startX,
      columnWidth,
      headerIdWidths,
    } = state.columnResizing;

    const deltaX = clientX - startX;
    const percentageDeltaX = deltaX / columnWidth;

    const newColumnWidths = {};

    const { instance } = this;

    headerIdWidths.forEach(([ headerId, headerWidth ]) => {
      const minWidth = instance.visibleColumns?.find?.(x => x.id === headerId)?.totalMinWidth || 0;
      const maxWidth = instance.visibleColumns?.find?.(x => x.id === headerId)?.totalMaxWidth || Number.MAX_SAFE_INTEGER;
      newColumnWidths[headerId] = Math.min(Math.max(
        headerWidth + headerWidth * percentageDeltaX,
        minWidth,
      ), maxWidth);
    });

    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        columnWidths: {
          ...state.columnResizing.columnWidths,
          ...newColumnWidths,
        },
      },
    };
  }

  if (action.type === actions.columnDoneResizing) {
    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        startX: null,
        isResizingColumn: null,
      },
    };
  }
}

const useInstanceBeforeDimensions = instance => {
  const {
    flatHeaders,
    disableResizing,
    getHooks,
    state: { columnResizing },
  } = instance;

  const getInstance = useGetLatest(instance);

  flatHeaders.forEach(header => {
    const canResize = getFirstDefined(
      header.disableResizing === true ? false : undefined,
      disableResizing === true ? false : undefined,
      true,
    );

    header.canResize = canResize;
    header.width =
      columnResizing.columnWidths[header.id] ||
      header.defaultWidth ||
      header.originalWidth ||
      header.width;
    header.isResizing = columnResizing.isResizingColumn === header.id;

    if (canResize) {
      header.getResizerProps = makePropGetter(getHooks().getResizerProps, {
        instance: getInstance(),
        header,
      });
    }
  });
};

function useInstance(instance) {
  const {
    plugins,
    dispatch,
    autoResetResize = true,
    columns,
  } = instance;

  ensurePluginOrder(plugins, [ "useAbsoluteLayout" ], "useResizeColumns");

  const getAutoResetResize = useGetLatest(autoResetResize);
  useMountedLayoutEffect(() => {
    if (getAutoResetResize()) {
      dispatch({ type: actions.resetResize });
    }
  }, [ columns ]);

  const resetResizing = useCallback(
    () => dispatch({ type: actions.resetResize }),
    [ dispatch ],
  );

  const setResizing = useCallback(
    (columnResizing) => dispatch({
      type: actions.setResize,
      payload: columnResizing,
    }),
    [ dispatch ],
  );

  useEffect(() => {
    columns.forEach(column => {
      if (column.defaultWidth === undefined) {
        column.defaultWidth = Math.max(column.minWidth, column.width);
      }
    });
  }, [ columns ]);

  Object.assign(instance, {
    resetResizing,
    setResizing,
  });
}

function getLeafHeaders(header) {
  const leafHeaders = [];
  const recurseHeader = header => {
    if (header.columns && header.columns.length) {
      header.columns.map(recurseHeader);
    }
    leafHeaders.push(header);
  };
  recurseHeader(header);
  return leafHeaders;
}
